import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  effect,
  inject,
  model,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';
import { debounceTime, filter, of, startWith, switchMap, take } from 'rxjs';

// froala plugins
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/entities.min.js';
import 'froala-editor/js/plugins/file.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/fullscreen.min.js';
import 'froala-editor/js/plugins/help.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/image_manager.min.js';
import 'froala-editor/js/plugins/inline_class.min.js';
import 'froala-editor/js/plugins/inline_style.min.js';
import 'froala-editor/js/plugins/line_breaker.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/paragraph_style.min.js';
import 'froala-editor/js/plugins/print.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/save.min.js';
import 'froala-editor/js/plugins/special_characters.min.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/js/plugins/url.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/js/plugins/word_paste.min.js';
// froala third party plugins
import 'froala-editor/js/third_party/image_tui.min';
import 'froala-editor/js/third_party/spell_checker.min';
// tribute
import Tribute, { TributeCollection } from 'tributejs';

import { KeyValueType } from '@models/common';
import { GenericObjectType } from '@models/helpers';
import { CipoTextConfig } from '@shared/components/fields/common';
import { FROALA_IMAGE_OPTIONS, FroalaConfig } from '@shared/components/froala/common';
import { CipoFroalaOptions, CipoTributeOptions, TributeReplaceEvent } from '@shared/components/froala/interfaces';
import { PlaceholdersDialogComponent } from '@shared/components/froala/placeholders-dialog/placeholders-dialog.component';
import { DIALOG_SIZE } from '@shared/consts';
import { UserService, UtilsService } from '@shared/services';
import { DictionaryStore } from '@signal-store/index';
import { makeKey } from '@signal-store/services/dictionary.service';
import { environment } from 'src/environments/environment';
import { CipoFormModule } from '../../modules/forms.module';
import { CipoControlBase } from '../fields/cipo-control.base';
import { CipoOutlineFieldComponent } from '../fields/cipo-outline-field/cipo-outline-field.component';

@Component({
  selector: 'app-froala',
  templateUrl: './froala.component.html',
  styleUrls: ['./froala.component.scss'],
  host: { '[class.fr-full-height]': 'fullHeight()' },
  imports: [
    CommonModule,
    FroalaEditorModule,
    CipoFormModule,
    FroalaViewModule,
    TranslateModule,
    CipoOutlineFieldComponent,
  ],
})
export class FroalaComponent extends CipoControlBase<string, CipoTextConfig> implements OnInit {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private dictionaryStore = inject(DictionaryStore);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  tribute: Tribute<KeyValueType>;
  tributeInitiliazed = false;
  isInitialValue = true;
  froalaElement: HTMLElement;
  //   save all events in this array and apply them on froala intialization
  froalaEvents: { name: string; callback: (...args) => void | boolean }[] = [];

  /*   Signal inputs
  editMode = input(true, { alias: 'editmode' });
  label = input('');
  hint = input('', {});
  required = input(false);
  invalid = computed(() => this.required() && !this.content() && !this.isInitialValue);
  content = model('');
  tributeOptions = input<CipoTributeOptions>({});
  */

  options = signal<CipoFroalaOptions>(FroalaConfig.basic);
  tributeOptions = model<CipoTributeOptions>({});
  content = signal('');
  readonly fullHeight = signal(false);

  @Input('fullheight')
  set _fullHeight(value: boolean) {
    this.fullHeight.set(value);
  }

  @Input('editmode')
  set _editMode(value: boolean) {
    this.fieldData.update(fd => ({ ...fd, readonly: !value }));
  }
  @Input('label')
  set _label(value: string) {
    this.fieldData.update(fd => ({ ...fd, label: value }));
  }
  @Input('hint')
  set _hint(value: string) {
    this.fieldData.update(fd => ({ ...fd, hint: value }));
  }
  @Input('required')
  set _required(value: boolean) {
    this.fieldData.update(fd => ({ ...fd, required: value }));
  }
  @Input('content')
  set _content(value: string) {
    if (this.control()) {
      this.control().setValue(value);
    }
    this.content.set(value);
  }
  /* Changed the name because of the concurency with separatedOptions on CipoControlBase. */
  @Input('froalaoptions')
  set _options(value: CipoFroalaOptions) {
    this.options.update(options => ({ ...options, ...value }));
  }
  @Input('tributeoptions')
  set _tributeoptions(value: CipoTributeOptions) {
    this.tributeOptions.set(value);
  }

  @HostListener('document:click')
  hideTributeMenuOnOutsideClick() {
    if (this.tribute?.isActive) {
      (this.tribute as unknown as { hideMenu: () => void }).hideMenu();
    }
  }

  @Output() contentChange = new EventEmitter<string>(); // for angular ngModel
  @Output() contentchange = new EventEmitter<string>(); // for angularJs

  controlValue = toSignal(
    toObservable(this.control).pipe(
      switchMap(ctrl => (ctrl ? ctrl.valueChanges.pipe(startWith(ctrl.value)) : of(null))),
    ),
  );

  private userService = inject(UserService);
  private utilsService = inject(UtilsService);

  constructor() {
    super();
    this.defineCustomIconTemplates();
    this.registerCustomCommands();
    this.initFroalaOptions();
    toObservable(this.tributeOptions)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.handleTributeUpdate();
      });
    effect(() => {
      const { contractId, moduleId } = untracked(this.fieldData) || {};

      if (contractId || moduleId) {
        const key = makeKey({ contractId, moduleId });
        const tags = this.dictionaryStore.predefined.tagUsers()?.[key]?.data;
        if (tags?.length) {
          /* This shows the info icon as a suffix, if there are any tags available to mention. 
          Removed the functionality per request. We might want this back */
          // this.fieldData.update(fd => ({ ...fd, fieldDescription: 'FROALA.mentionsHint' }));
          untracked(() => {
            this.tributeOptions.update(options => ({ ...options, mentions: this.utilsService.mapArrayToObject(tags) }));
          });
        }
      }
    });
    effect(() => {
      this.contentChange.emit(this.content());
      this.contentchange.emit(this.content());
    });
    toObservable(this.content)
      .pipe(
        filter(val => !!val),
        debounceTime(100),
        take(1),
      )
      .subscribe(() => {
        this.isInitialValue = false;
        if (this.froalaElement) {
          this.validateVariablesAndMentions(this.froalaElement);
        }
      });

    effect(() => {
      const controlValue = this.controlValue();
      const control = untracked(() => this.control());
      if (control && controlValue !== untracked(() => this.content())) {
        this.content.set(controlValue);
      }
    });

    effect(() => {
      const control = untracked(() => this.control());
      if (!control) {
        return;
      }
      const content = this.content();
      if (control.value !== content) {
        control.setValue(content);
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });

    effect(() => {
      if (!this.fieldData()?.position?.rows) {
        return;
      }
      const heightMax = this.maxHeight();
      this.options.update(options => ({ ...options, heightMax }));
    });

    effect(() => {
      if (!this.deepFieldData.readonly()) {
        this.addEventsInitialized();
      }
    });

    /* For froala we don't need to set the max length validator, because froala has its own char counter */
    effect(() => {
      const maxLength = this.deepFieldData.maxLength();
      if (maxLength) {
        this.setMaxLengthValidator(0);
        this.options.update(options => ({ ...options, charCounterMax: maxLength }));
      }
    });

    /* Set editor settings based on fieldData. 
    Commented this for now to see if we still need it.
    This was for dynamic form froala fields, to determine what toolbars to show, but if we choose to keep the dynamic froala inline, we are not limited by it's container */
    // effect(() => {
    //   let currentOptions = untracked(() => this.options());
    //   const { rows, cols } = this.fieldData()?.position || {};
    //   /* If toolbar inline or the cols or rows are not defined - exit */
    //   if (currentOptions.toolbarInline || !rows || !cols) {
    //     return;
    //   }
    //   /*
    //     Initial height is 82px - this equals 2 rows of text using the default font size + paddings
    //     Then we add 42px for each extra position row (2 more paragraph rows).
    //    */
    //   const newHeight = 82 + (rows - 1) * 42;
    //   currentOptions.heightMax = newHeight;
    //   currentOptions.heightMin = newHeight - 1; // -1 is needed to remove some weird scroll from editable area
    //   if (cols < 6) {
    //     currentOptions = { ...currentOptions, ...FROALA_TOOLBAR_MEDIUM };
    //   }
    //   this.options.set(currentOptions);
    // });
  }

  ngOnInit(): void {
    this.addEventsInitialized();
  }

  private handleTributeUpdate() {
    if (this.tributeOptions()) {
      if (this.tributeInitiliazed) {
        this.updateTributeCollections();
      } else {
        this.addTributeFunctionality();
      }
    }
  }

  defineCustomIconTemplates() {
    FroalaEditor.DefineIconTemplate('mat-icons', '<span class="material-icons">[NAME]</span>');
    FroalaEditor.DefineIconTemplate('mat-icons-outlined', '<span class="material-icons-outlined">[NAME]</span>');
    FroalaEditor.DefineIconTemplate('mat-symbols', '<span class="material-symbols">[NAME]</span>');
    FroalaEditor.DefineIconTemplate('mat-symbols-outlined', '<span class="material-symbols-outlined">[NAME]</span>');
  }

  registerCustomCommands() {
    const openPlaceholdersDialog = (editor: FroalaEditor) => this.openPlaceholdersDialog(editor);
    const froalaShapes = {
      'circle.jpg': 'Circle',
      'square.jpg': 'Square',
      'triangle.jpg': 'Triangle',
      'torque-sequencing.jpg': 'Torque Sequencing',
    };

    FroalaEditor.DefineIcon('shapes', { template: 'mat-icons-outlined', NAME: 'extension' });
    FroalaEditor.RegisterCommand('shapes', {
      title: 'Shapes',
      type: 'dropdown',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      options: froalaShapes,
      callback: function (_, filename) {
        const link = `${window.location.origin}/Content/images/shapes/${filename}`;
        (this as { html: { insert: (s: string) => void } }).html.insert(`<img src="${link}" />`);
      },
    });

    FroalaEditor.DefineIcon('placeholders', { template: 'mat-icons-outlined', NAME: 'data_object' });
    FroalaEditor.RegisterCommand('placeholders', {
      title: 'Print Variables',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        openPlaceholdersDialog(this);
      },
    });

    FroalaEditor.RegisterCommand('print', {
      title: 'Print',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        const printSection = document.getElementById('section-to-print');
        if (printSection) {
          document.body.removeChild(printSection);
        }

        const div = document.createElement('div');
        div.id = 'section-to-print';
        div.className = 'fr-view cipo--display--none';
        div.innerHTML = this.html.get();
        document.body.append(div);

        setTimeout(function () {
          window.print();
        }, 400);
      },
    });
  }

  focusEditor() {
    if (this.froalaElement) {
      this.control()?.markAsTouched();
      this.froalaElement.focus();
    }
  }

  /** Opens the placeholders picker and inserts the selection at the cursor position, the same way typing `#` does through tribute. */
  openPlaceholdersDialog(editor: FroalaEditor) {
    const { variables, variableDescriptions } = this.tributeOptions();
    if (!variables || !Object.keys(variables).length) {
      return;
    }
    // preserve the cursor position, since opening the dialog moves focus away from the editor
    editor.selection.save();
    this.dialog
      .open(PlaceholdersDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...DIALOG_SIZE.lg,
        // a definite height (not just min/max) is required for the inner list to size itself and scroll, instead of growing past the dialog bounds
        height: DIALOG_SIZE.lg.maxHeight,
        data: { variables, variableDescriptions },
      })
      .afterClosed()
      .subscribe((selected: KeyValueType) => {
        editor.selection.restore();
        if (!selected) {
          return;
        }
        const { key, value } = selected;
        const html = `<span class="parse-variables" id="${key}"><span class="print">{{${key}}}</span><span class="no-print">${value}</span></span>`;
        editor.html.insert(html, true);
        editor.events.trigger('contentChanged', []);
        const insertedSpan = editor.el.querySelector(`#${this.escapeSelectorId(key)}`) as HTMLElement;
        this.handleSelectOnClick(insertedSpan);
      });
  }

  /** Escapes characters that are invalid in a CSS id selector (variable keys can contain dots, brackets, etc). */
  private escapeSelectorId(id: string): string {
    return id.replace(/([^\w\d]|\\|:|\.|\[|\]|\(|\)|\+|>|~|,|^|\|)/g, '\\$1');
  }

  initFroalaOptions() {
    this.options.set({
      ...FroalaConfig.basic,
      requestHeaders: this.userService.getUserHeaders(),
      ...FROALA_IMAGE_OPTIONS,
      key: environment.froalaKey,
      scaytCustomerId: environment.scaytCustomerId,
    });
  }

  validateVariablesAndMentions(targetElement?: HTMLElement) {
    if (!(this.content()?.includes('class="parse-variables') || this.content()?.includes('class="tag-user"'))) {
      return;
    }
    const { variables, mentions } = this.tributeOptions();

    if (variables && Object.keys(variables).length >= 0) {
      const variablesSpans = targetElement.querySelectorAll<HTMLElement>('.parse-variables');
      variablesSpans.forEach(span => {
        const key = span.getAttribute('id');
        if (!variables[key]) {
          span.removeAttribute('id');
          // keep the parse-variables class so the hover listener (and re-validation) can still find this span
          span.className = 'parse-variables cipo-bg--warn-100';
          span.innerHTML = `{{ ${span.lastChild.textContent} }}`;
        } else if (variables[key] !== span.lastChild.textContent) {
          span.lastChild.textContent = variables[key];
        }
        this.handleSelectOnClick(span);
      });
    }

    if (mentions && Object.keys(mentions).length >= 0) {
      const mentionsSpans = targetElement.querySelectorAll('.tag-user');
      mentionsSpans.forEach((span: HTMLElement) => {
        const key = span.getAttribute('id');
        if (!mentions[key]) {
          span.removeAttribute('id');
          span.innerHTML = `@${span.textContent}`;
          span.className = 'cipo-bg--warn-100';
        } else if (mentions[key] !== span.textContent) {
          span.innerHTML = mentions[key];
        }
        this.handleSelectOnClick(span);
      });
    }
  }

  /** Shows a variable's current description as a native tooltip on hover, without persisting it into the saved HTML.
   * Delegated on the stable editor root (instead of attaching per-span) because SCAYT's spellcheck pass periodically
   * rebuilds the spans' inner nodes, which would silently drop any listener attached directly on them. */
  attachVariableHoverListener(root: HTMLElement) {
    root.addEventListener('mouseover', (event: MouseEvent) => {
      const span = (event.target as HTMLElement)?.closest?.('.parse-variables') as HTMLElement;
      if (!span) {
        return;
      }
      // validateVariablesAndMentions strips the id when the variable is no longer in the current dictionary
      const key = span.getAttribute('id');
      if (!key) {
        span.setAttribute('title', this.translate.instant('FROALA.placeholders.deletedPlaceholder'));
        return;
      }
      const description = this.tributeOptions().variableDescriptions?.[key];
      if (description) {
        span.setAttribute('title', description);
      }
    });
    root.addEventListener('mouseout', (event: MouseEvent) => {
      (event.target as HTMLElement)?.closest?.('.parse-variables')?.removeAttribute('title');
    });
  }

  handleSelectOnClick(span: HTMLElement) {
    if (!span) {
      return;
    }
    span.addEventListener('click', () => {
      const range = document.createRange();
      range.selectNodeContents(span);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    });
  }

  addTributeFunctionality() {
    const { mentions, variables, variableDescriptions } = this.tributeOptions();
    const collections: TributeCollection<KeyValueType>[] = [];

    if (variables) {
      collections.push({
        values: this.buildVariableTributeValues(variables, variableDescriptions),
        trigger: '#',
        selectTemplate: ({ original: { key, value } }) =>
          // moved view span as last one, because backspace removes characters from the last inserted key/value, wich in our case was printing key.
          `<span class="parse-variables" id="${key}"><span class="print">{{${key}}}</span><span class="no-print">${value}</span></span>`,
        menuItemTemplate: item => {
          const { value, description } = item.original as KeyValueType & { description?: string };
          // .tribute-container li span is bolded by default; force normal weight so only description-less items don't stand out
          return description
            ? `<span title="${this.escapeHtmlAttribute(description)}" style="font-weight: normal">${value}</span>`
            : value;
        },
        lookup: this.customTributeSearch,
      });
    }

    if (mentions) {
      collections.push({
        values: this.utilsService.mapObjectToArray(mentions),
        trigger: '@',
        selectTemplate: item => `<span class="tag-user" id=${item.original.key}>${item.original.value}</span>`,
        menuItemTemplate: item => item.original.value,
        lookup: this.customTributeSearch,
      });
    }

    if (collections.length) {
      this.initTribute(collections);
    }
  }

  customTributeSearch(item: KeyValueType) {
    return item.value.replaceAll(' ', '');
  }

  /** Merges each variable's description (used by the print variables picker) so the `#` tribute list can show it as a hover title. */
  private buildVariableTributeValues(
    variables: GenericObjectType,
    variableDescriptions?: GenericObjectType,
  ): (KeyValueType & { description?: string })[] {
    return this.utilsService.mapObjectToArray(variables).map(item => ({
      ...item,
      description: variableDescriptions?.[item.key],
    }));
  }

  private escapeHtmlAttribute(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  initTribute(collection: TributeCollection<KeyValueType>[]) {
    this.tribute = new Tribute({ collection });
    this.tributeInitiliazed = true;

    this.froalaEvents.push(
      {
        name: 'keydown',
        callback: (event: KeyboardEvent) => {
          if (event.key === 'Enter' && this.tribute.isActive) {
            return false;
          }
        },
      },
      {
        name: 'contentChanged',
        callback: () => {
          if (this.froalaElement.querySelector('.fr-marker')) {
            return;
          }
          this.froalaElement.querySelectorAll('.parse-variables:not(:has(.no-print))').forEach(span => span.remove());
        },
      },
    );
  }

  updateTributeCollections() {
    const tributeCollections = (this.tribute as unknown as { collection: TributeCollection<KeyValueType>[] })
      .collection;
    const { mentions, variables, variableDescriptions } = this.tributeOptions();
    tributeCollections.forEach(collection => {
      if (collection.trigger === '#' && variables) {
        collection.values = this.buildVariableTributeValues(variables, variableDescriptions);
      } else if (collection.trigger === '@' && mentions) {
        collection.values = this.utilsService.mapObjectToArray(mentions);
      }
    });
  }

  addEventsInitialized() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisRef = this;
    const options = this.options();
    options.events = {
      initialized: function () {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const editor = this;
        thisRef.froalaElement = editor.el;
        thisRef.attachVariableHoverListener(editor.el);

        if (thisRef.fullHeight()) {
          const el = thisRef.elementRef.nativeElement;
          const hostHeight = el.getBoundingClientRect().height;
          const toolbar = el.querySelector('.fr-toolbar') as HTMLElement;
          const footer = el.querySelector('.fr-second-toolbar') as HTMLElement;
          const extraSpace = (toolbar?.offsetHeight ?? 0) + (footer?.offsetHeight ?? 0);
          const editorHeight = Math.floor(hostHeight) - extraSpace;
          if (editorHeight > 0) {
            const wrapper = el.querySelector('.fr-wrapper') as HTMLElement;
            if (wrapper) {
              wrapper.style.minHeight = `${editorHeight}px`;
              wrapper.style.maxHeight = `${editorHeight}px`;
            }
          }
        }

        if (thisRef.tribute) {
          thisRef.tribute.attach(editor.el);
          thisRef.validateVariablesAndMentions(editor.el);
          editor.el.addEventListener('tribute-replaced', ({ target, detail: { item } }: TributeReplaceEvent) => {
            const insertedSpan = target.querySelector(`#${thisRef.escapeSelectorId(item.original.key)}`) as HTMLElement;
            thisRef.handleSelectOnClick(insertedSpan);
          });
        }
        /* 
            populate this array with events that are needed to be triggered before others
            Documentation: first - Trigger the callback before already registered callbacks.
        */
        const firstEvents = ['keydown'];
        thisRef.froalaEvents.forEach(event => {
          const isFirstEvent = firstEvents.includes(event.name);
          this.events.on(event.name, event.callback, isFirstEvent);
        });

        this.events.on('commands.after', cmd => {
          if (cmd === 'html') {
            // when switching back from view code, validate variables and mentions
            thisRef.validateVariablesAndMentions(this.el);
          }

          // Fix SCAYT interference for commands that Froala handles natively with active selection
          // (e.g. fontSize uses a dropdown → selection stays → Froala applies style directly onto
          // the SCAYT span. html.get() strips SCAYT spans and loses the style.)
          if (cmd === 'fontSize' || cmd === 'fontFamily') {
            (
              thisRef.froalaElement?.querySelectorAll('span.wsc-spelling-problem[style]') as NodeListOf<HTMLElement>
            )?.forEach(scaytSpan => {
              const style = scaytSpan.getAttribute('style');
              if (!style) return;
              const wrapper = document.createElement('span');
              wrapper.setAttribute('style', style);
              scaytSpan.removeAttribute('style');
              scaytSpan.parentNode?.insertBefore(wrapper, scaytSpan);
              wrapper.appendChild(scaytSpan);
            });
          }
        });

        this.events.on('commands.before', function (cmd, param1) {
          if (cmd === 'fontSize' || cmd === 'fontFamily') {
            const sel = window.getSelection();
            const anchor = sel?.anchorNode;
            const anchorEl = anchor instanceof Element ? anchor : anchor?.parentElement;
            const targetVar = anchorEl?.closest('.parse-variables') as HTMLElement;

            if (targetVar) {
              // Selection is inside a parse-variables span. Apply the style directly to the
              // inner spans and cancel Froala's native command.
              // Froala fires fontSize/fontFamily twice when fr-selected-cell is present: once for
              // the text selection (correct) and once for the cell (which destroys the variable).
              // Returning false here cancels Froala's execution entirely.
              const noPrint = targetVar.querySelector('.no-print') as HTMLElement;
              const print = targetVar.querySelector('.print') as HTMLElement;
              if (cmd === 'fontSize') {
                if (noPrint) noPrint.style.fontSize = param1;
                if (print) print.style.fontSize = param1;
              } else {
                if (noPrint) noPrint.style.fontFamily = param1;
                if (print) print.style.fontFamily = param1;
              }
              editor.undo.saveStep();
              return false;
            }
          }

          /*
           * Fix: When Froala opens a popup (e.g., color picker), it saves the selection
           * via .fr-marker elements. When the user picks a color, the apply* command fires
           * but the selection is empty. Froala's internal applytextColor fails silently.
           *
           * We bypass Froala's broken handler entirely: restore the selection from markers,
           * apply the color using document.execCommand, and return false to cancel Froala's handler.
           */
          if (cmd === 'applytextColor' || cmd === 'applybackgroundColor') {
            const sel = window.getSelection();
            const hasMarkers = thisRef.froalaElement?.querySelector('.fr-marker');
            if (hasMarkers && (!sel || sel.toString() === '')) {
              editor.selection.restore();

              if (window.getSelection()?.toString()) {
                document.execCommand('styleWithCSS', false, 'true');

                if (cmd === 'applytextColor') {
                  document.execCommand('foreColor', false, param1);
                } else {
                  document.execCommand('hiliteColor', false, param1);
                }

                // Fix SCAYT interference: execCommand applies style onto the SCAYT span.
                // Wrap any SCAYT span with inline style in a proper styled span so
                // html.get() preserves the color when it strips the SCAYT markup.
                (
                  thisRef.froalaElement?.querySelectorAll('span.wsc-spelling-problem[style]') as NodeListOf<HTMLElement>
                )?.forEach(scaytSpan => {
                  const style = scaytSpan.getAttribute('style');
                  if (!style) return;
                  const wrapper = document.createElement('span');
                  wrapper.setAttribute('style', style);
                  scaytSpan.removeAttribute('style');
                  scaytSpan.parentNode?.insertBefore(wrapper, scaytSpan);
                  wrapper.appendChild(scaytSpan);
                });

                editor.undo.saveStep();
                editor.popups.hideAll();
                return false;
              }
            }
          }
        });

        const onScroll = (event: Event) => {
          if (!thisRef.options().toolbarInline) return;
          // Only hide toolbar when the editor's own container scrolls.
          // Ignore scroll events triggered by unrelated elements (e.g. PSPDFKit calling .focus()).
          const target = event.target as Node;
          if (target === document || (target instanceof HTMLElement && target.contains(editor.el))) {
            editor.toolbar.hide();
          }
        };

        window.addEventListener('scroll', onScroll, true);

        editor.events.on('destroy', () => {
          window.removeEventListener('scroll', onScroll, true);
        });
      },
    };
    this.options.set(options);
  }
}
